import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Fade from '@mui/material/Fade';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { formatDate, shortenedDate } from 'Consts/DateFormats';
import useMobileLayout from 'Hooks/useMobileLayout';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { loadNextResourceSet } from 'Context/PagedCollectionContext';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import { FileViewer }
  from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer';
import { STATEMENT_TAX_TAB_LABEL } from 'Components/UI/Accounts/ManagedAccount/Context/ManagedAccountTabContext';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import TableView from './TableView';
import {
  useManagedAccountStatementDocumentsDispatchContext,
  useManagedAccountStatementDocumentsStateContext,
} from '../../Context/ManagedAccountStatementDocumentsContext';
import {
  useManagedAccountTaxDocumentsDispatchContext,
  useManagedAccountTaxDocumentsStateContext,
} from '../../Context/ManagedAccountTaxDocumentsContext';
import {
  useManagedAccountTradeConfirmationDocumentsDispatchContext,
  useManagedAccountTradeConfirmationDocumentsStateContext,
} from '../../Context/ManagedAccountTradeConfirmationDocumentsContext';

const styles = (theme) => ({
  root: {},
  tableContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  tableMobile: {
    flexBasis: '100%',
    marginBottom: theme.spacing(4),
  },
  table: {
    flexBasis: '32%',
  },
  documentButton: {
    width: '100%',
    minWidth: 0,
    '& .MuiButton-label': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textAlign: 'right',
      display: 'block',
    },
  },
});

// eslint-disable-next-line
export const TabLoadingIndicator = ({label, classes}) => {
  return (
    <div className={classes.tabLoading}>
      <Typography
        variant={'h5'}
        color={'primary'}
        weight={'bold'}
      >
        {`Fetching your ${label}...`}
      </Typography>
      <LoadingIndicator />
    </div>
  );
};

function trimFileExtension(name) {
  return name.substring(0, name.lastIndexOf('.'));
}

const StatementsTaxDocsTabContent = ({
  className,
  classes,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [documentResources, setDocumentResources] = useState(null);

  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Managed Account Statements & Tax Documents Tab',
    isPageView: true,
  });

  const isMobile = useMobileLayout();

  const statementDocumentsStateContext = useManagedAccountStatementDocumentsStateContext();
  const statementDocumentsDispatchContext = useManagedAccountStatementDocumentsDispatchContext();

  const taxDocumentsStateContext = useManagedAccountTaxDocumentsStateContext();
  const taxDocumentsDispatchContext = useManagedAccountTaxDocumentsDispatchContext();

  const tradeConfirmationDocumentsStateContext = useManagedAccountTradeConfirmationDocumentsStateContext();
  const tradeConfirmationDocumentsDispatchContext = useManagedAccountTradeConfirmationDocumentsDispatchContext();

  // Document Link
  const renderDocumentLink = (data) => {
    return (
      <Button
        className={classes.documentButton}
        color={'accentBlue2'}
        onClick={() => {
          setDocumentResources(data.documentResources);
          setIsPreviewOpen(true);
        }}
        test={'managed-account-preview-document-button'}
      >
        {trimFileExtension(data.link)}
      </Button>
    );
  };

  const getTableData = resources => resources.map((document) => ({
    date: document.attr('documentDate'),
    link: document.attr('name'),
    documentResources: [document],
  }));

  const TABLE_COLUMNS = [
    {
      title: 'Date',
      key: 'date',
      render: data => formatDate(data.date, shortenedDate),
    },
    {
      title: 'Link',
      key: 'link',
      render: data => renderDocumentLink(data),
    },
  ];

  if (statementDocumentsStateContext.loading || taxDocumentsStateContext.loading || tradeConfirmationDocumentsStateContext.loading) {
    return <TabLoadingIndicator label={STATEMENT_TAX_TAB_LABEL} classes={classes} />;
  }

  return (
    <Fade
      in
      timeout={500}
    >
      <div
        className={classNames(classes.root, className)}
        {...rootNodeProps}
      >
        {documentResources && (
          <FileViewer
            attachmentResources={documentResources}
            isOpen={isPreviewOpen}
            closeModal={() => {
              setDocumentResources(null);
              setIsPreviewOpen(false);
            }}
            eventCategory={'Managed Account Statements & Tax Documents Tab'}
          />)
        }
        <div
          className={classNames(classes.tableContainer, {
            [classes.tableMobile]: isMobile,
          })}
        >
          <TableView
            className={classNames({
              [classes.tableMobile]: isMobile,
              [classes.table]: !isMobile,
            })}
            type={'Statements'}
            columns={TABLE_COLUMNS}
            data={getTableData(statementDocumentsStateContext.resources)}
            resources={statementDocumentsStateContext.resources}
            LoadMoreButtonProps={{
              hasMoreItems: statementDocumentsStateContext.hasNextLink,
              loading: statementDocumentsStateContext.loading,
              loadMoreItems: () => loadNextResourceSet(statementDocumentsDispatchContext, statementDocumentsStateContext),
            }}
          />
          <TableView
            className={classNames({
              [classes.tableMobile]: isMobile,
              [classes.table]: !isMobile,
            })}
            type={'Tax Documents'}
            columns={TABLE_COLUMNS}
            data={getTableData(taxDocumentsStateContext.resources)}
            resources={taxDocumentsStateContext.resources}
            LoadMoreButtonProps={{
              hasMoreItems: taxDocumentsStateContext.hasNextLink,
              loading: taxDocumentsStateContext.loading,
              loadMoreItems: () => loadNextResourceSet(taxDocumentsDispatchContext, taxDocumentsStateContext),
            }}
          />
          <TableView
            className={classNames({
              [classes.tableMobile]: isMobile,
              [classes.table]: !isMobile,
            })}
            type={'Trade Confirmations'}
            columns={TABLE_COLUMNS}
            data={getTableData(tradeConfirmationDocumentsStateContext.resources)}
            resources={tradeConfirmationDocumentsStateContext.resources}
            LoadMoreButtonProps={{
              hasMoreItems: tradeConfirmationDocumentsStateContext.hasNextLink,
              loading: tradeConfirmationDocumentsStateContext.loading,
              loadMoreItems: () => loadNextResourceSet(tradeConfirmationDocumentsDispatchContext, tradeConfirmationDocumentsStateContext),
            }}
          />
        </div>
      </div>
    </Fade>
  );
};

StatementsTaxDocsTabContent.defaultProps = {
  className: '',
};

StatementsTaxDocsTabContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  collection: PropTypes.array.isRequired,
};

export default withStyles(styles)(StatementsTaxDocsTabContent);
